import type { KambiSearchTermsView } from '@mobi/api-types/kambi'
import { simpleFetch } from '@mobi/utils/fetch/simpleFetch'
import type { QueryClient } from '@tanstack/react-query'
import { getSearchResult } from './listView'

type ResultTerm = KambiSearchTermsView['resultTerms']['0']

export async function getSportsTermSearch(
  queryClient: QueryClient,
  searchTerm: string,
  baseQueryKey: string[],
  queryCacheTime: number
) {
  const searchResults = await simpleFetch<KambiSearchTermsView>(
    `${window._kc.apiOfferingBaseUrl}term/search?lang=en_AU&market=AU&term=${searchTerm}`
  )

  const searchLower = searchTerm.toLowerCase()

  const promises = searchResults.resultTerms.map(async res => {
    res.value = res.localizedName

    try {
      const itemResult = await queryClient.fetchQuery({
        queryKey: [...baseQueryKey, res.id],
        queryFn: () => getSearchResult(res.type === 'REGION' ? res.parentId : res.id),
        staleTime: queryCacheTime,
        gcTime: queryCacheTime,
      })

      res.icon = itemResult.events[0]?.event.sport

      if (
        !res.value.toLowerCase().includes(searchLower) &&
        (res.type === 'LEAGUE' || res.type === 'REGION' || res.type === 'SPORT')
      ) {
        const event = itemResult.events.find(x => x.event.name.toLowerCase().includes(searchLower))
        if (event) {
          res.secondaryValue = event.event.name
        } else {
          const path = itemResult.events[0]?.event.path.find(x =>
            x.name.toLowerCase().includes(searchLower)
          )
          res.secondaryValue = path?.name
        }
        return [res]
      } else if (res.type === 'PARTICIPANT') {
        const searchedEvents = itemResult.events
          .filter(x => x.event.name.toLowerCase().includes(searchLower))
          .map(ev => {
            const path = '/' + ev.event.path.map(x => x.termKey).join('/')
            const id = `${path}/event/${ev.event.id}?participant=${encodeURIComponent(res.localizedName)}`

            const r: ResultTerm = {
              type: 'EVENT',
              id,
              termKey: res.termKey,
              localizedName: ev.event.name,
              parentId: path,
              englishName: ev.event.englishName,
              value: ev.event.name,
              icon: ev.event.sport,
            }
            return r
          })

        return [res, ...searchedEvents]
      }

      return [res]
    } catch {
      // ignore it
      return [res]
    }
  })

  const res = await Promise.all(promises)

  // only show the events the participants are in, not the participants
  searchResults.resultTerms = res.flatMap(x => x).filter(x => x.type !== 'PARTICIPANT')

  // remove duplicate items
  const seenItems = new Set<string>()
  for (let index = 0; index < searchResults.resultTerms.length; ) {
    const element = searchResults.resultTerms[index]
    if (seenItems.has(element.id)) {
      searchResults.resultTerms.splice(index, 1)
      continue
    }
    seenItems.add(element.id)
    index++
  }

  return searchResults
}
